import { Box, Table, TableBody, TableHead } from "@mui/material";
import type { ReactNode } from "react";

/**
 * Reusable premium table shell — header row in soft surface, body on
 * the elevated surface (one tone lighter than the page background, so
 * the table reads as a card, not flat-on-page). Pages pass
 * `<SupportTable head={<TableRow>…</TableRow>}>` with the body
 * `<TableRow>`s as children. Keeps every list page visually identical
 * and matches the chrome the CRM tables already use.
 *
 * The header row's cells are styled centrally here (soft surface, the
 * small uppercase muted label treatment) so callers only describe the
 * column labels, not the chrome.
 */
export function SupportTable({
  head,
  children,
  empty,
}: {
  head: ReactNode;
  children: ReactNode;
  /** Rendered inside a centred `<TableRow>` when the caller has zero rows. */
  empty?: ReactNode;
}): JSX.Element {
  return (
    <Box
      sx={{
        // Horizontal scroll on narrow screens instead of clipping the wide
        // table (was `overflow: "hidden"`, which hid columns on mobile).
        // `overflowX: auto` still clips to the rounded corners.
        overflowX: "auto",
        borderRadius: 1,
        border: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
        boxShadow: (t) => t.shadows[1],
      }}
    >
      <Table
        size="small"
        sx={{
          width: "100%",
          fontSize: 14,
          // Header chrome: soft surface, small uppercase muted labels, a
          // hairline divider under the head — applied via the head row so
          // callers don't repeat it per cell.
          "& thead th": {
            borderBottom: 1,
            borderColor: "divider",
            bgcolor: "surfaceSoft",
            fontSize: 12,
            fontWeight: 500,
            textTransform: "uppercase",
            letterSpacing: "0.04em",
            color: "text.disabled",
          },
          // Body cells default to the same small padding the old table used.
          "& td, & th": { px: 1.5, py: 1 },
        }}
      >
        <TableHead>{head}</TableHead>
        <TableBody>
          {children}
          {empty}
        </TableBody>
      </Table>
    </Box>
  );
}
